import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, SectionTag } from "@/components/Reveal";
import { useSiteContent } from "@/context/ContentContext";

export default function CaseStudy() {
    const { caseStudies, CONTENT_LOADED } = useSiteContent();
    const { slug } = useParams();
    if (!CONTENT_LOADED) return <div className="min-h-screen" data-testid="case-study-loading" />;
    const index = caseStudies.findIndex((s) => s.slug === slug);
    if (index === -1) return <Navigate to="/work" replace />;
    const study = caseStudies[index];
    const next = caseStudies[(index + 1) % caseStudies.length];
    const domain = study.url.replace(/^https?:\/\//, "").replace(/^www\./, "");

    return (
        <div data-testid="case-study-page">
            <SEO
                title={`${study.name} Website — Case Study | Camarca Digital Designs`}
                description={`${study.headline} See the ${study.name} website designed and built by Camarca Digital Designs — ${study.category.toLowerCase()} site with ${study.features[0].toLowerCase()}, ${study.features[1].toLowerCase()} and more.`}
                path={`/work/${study.slug}`}
            />

            <header className="mx-auto max-w-7xl px-6 pb-14 pt-36 lg:px-10">
                <Reveal>
                    <Link
                        to="/work"
                        data-testid="case-study-back-link"
                        className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 transition-colors duration-300 hover:text-neon"
                    >
                        <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
                        All Work
                    </Link>
                    <div className="mt-10">
                        <SectionTag>
                            Case Study — {study.category}{study.location ? ` · ${study.location}` : ""}
                        </SectionTag>
                        <h1 className="mt-6 font-display text-4xl font-black leading-tight tracking-tight md:text-6xl">
                            {study.name}
                        </h1>
                        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-zinc-400 md:text-xl">
                            {study.headline}
                        </p>
                    </div>
                </Reveal>
            </header>

            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <Reveal>
                    <div className="clip-frame overflow-hidden border border-white/10">
                        <img
                            src={study.image}
                            alt={study.alt}
                            data-testid="case-study-hero-image"
                            className="aspect-[16/9] w-full object-cover object-top"
                        />
                    </div>
                </Reveal>

                <div className="mt-16 grid gap-12 lg:grid-cols-12">
                    <Reveal className="lg:col-span-7">
                        <SectionTag>The Story</SectionTag>
                        <div className="mt-6 space-y-6">
                            {study.story.map((p, i) => (
                                <p key={i} className="leading-relaxed text-zinc-300 md:text-lg">{p}</p>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-5">
                        <div className="border border-white/10 bg-surface p-8">
                            <p className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500">What Was Built</p>
                            <ul className="mt-6 space-y-3">
                                {study.features.map((f) => (
                                    <li key={f} className="flex items-center gap-3 text-sm text-zinc-300">
                                        <Check className="h-4 w-4 shrink-0 text-neon" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            <a
                                href={study.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="case-study-live-link"
                                className="group mt-8 flex items-center justify-center gap-3 bg-neon px-6 py-4 font-mono text-xs font-medium tracking-[0.2em] uppercase text-void transition-shadow duration-300 hover:shadow-[0_0_25px_rgba(0,240,255,0.5)]"
                            >
                                Visit Live Site
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </a>
                            <p className="mt-4 text-center font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-600">
                                Live at {domain}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>

            <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
                <Reveal className="flex flex-wrap items-center justify-between gap-8 border border-white/10 bg-surface p-8 md:p-10">
                    <div>
                        <h2 className="font-display text-2xl font-bold md:text-3xl">Want a site like this?</h2>
                        <p className="mt-3 max-w-lg text-zinc-400">
                            Tell me about your business and I'll build you something worth a case study.
                        </p>
                    </div>
                    <Link
                        to="/contact"
                        data-testid="case-study-quote-cta"
                        className="group flex items-center gap-3 border border-neon/70 px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase text-neon transition-[background-color,color,box-shadow] duration-300 hover:bg-neon hover:text-void hover:shadow-[0_0_25px_rgba(0,240,255,0.4)]"
                    >
                        Start a Project
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </Reveal>
            </section>

            <Link
                to={`/work/${next.slug}`}
                data-testid="case-study-next-link"
                className="group block border-t border-white/10 bg-void transition-colors duration-500 hover:bg-surface"
            >
                <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-14 lg:px-10">
                    <div>
                        <p className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500">Next Project</p>
                        <p className="mt-3 font-display text-2xl font-bold transition-colors duration-300 group-hover:text-neon md:text-4xl">
                            {next.name}
                        </p>
                    </div>
                    <ArrowRight className="h-8 w-8 text-zinc-600 transition-[color,transform] duration-300 group-hover:translate-x-2 group-hover:text-neon" />
                </div>
            </Link>
        </div>
    );
}
